import { useEffect, useState } from "react";
import { Modal, Form, Input, Select, InputNumber, DatePicker, Row, Col, Card, Space } from "antd";
import { FileTextOutlined, DollarOutlined, ShopOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { createOrdenServicioPO, updateOrdenServicioPO, getEstadosPO, getUnidadesMedidaPO } from "../../../../services/financiero/ingresosPO/ordenesServicioPOService";
import { getProveedoresPO } from "../../../../services/financiero/ingresosPO/proveedoresPOService";

const cardHeaderStyle = { background: "#042956", color: "#fff", borderRadius: "10px 10px 0 0", minHeight: 42 };
const cardStyle = { borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 20 };

const CardTitle = ({ icon, children }) => (
  <Space size={8}>
    {icon}
    <span style={{ fontWeight: 600, fontSize: 13 }}>{children}</span>
  </Space>
);

export default function OrdenServicioPOForm({ open, onClose, onSuccess, producto, userLogin }) {

  const [form] = Form.useForm();
  const [proveedores, setProveedores] = useState([]);
  const [estados, setEstados] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const estadoMap = {
    "FINALLY CLOSED": "Entregado",
    "CLOSED": "Cerrado",
    "CLOSED FOR RECEIVING": "Cerrado para recepción",
    "OPEN": "Pendiente",
    "PENDING": "Pendiente"
  };
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    cargarCatalogos();
  }, []);

  useEffect(() => {
    if (producto) {
      form.setFieldsValue(producto);
    } else {
      form.resetFields();
    }
  }, [producto]);

  const cargarCatalogos = async () => {
    try {
      const prov = await getProveedoresPO();
      const est = await getEstadosPO();
      const uni = await getUnidadesMedidaPO();
      setProveedores(prov);
      setEstados(est);
      setUnidades(uni);
    } catch (err) {
      console.error(err);
    }
  }

  const handleSubmit = async (values) => {
    try {
      values.fecha_estimada_entrega = values.fecha_estimada_entrega?.format("YYYY-MM-DD");
      if (producto) {
        await updateOrdenServicioPO(producto.oid, values, userLogin);
      } else {
        await createOrdenServicioPO(values, userLogin);
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
    }
  }

  const checkForm = () => {
    const values = form.getFieldsValue();
    const requiredFields = ["Numero_PO", "nombre_producto", "cantidad", "oid_proveedor"];
    const valid = requiredFields.every(field => values[field]);
    setIsValid(valid);
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okButtonProps={{ disabled: !isValid }}
      width={900}
      title={producto ? "Editar orden de servicio" : "Registrar nueva orden de servicio"}
      okText="Guardar"
      cancelText="Cancelar"
      destroyOnClose
    >
      <Form form={form} layout="vertical" component={false} onFinish={handleSubmit} onValuesChange={checkForm}>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 20 } }}
          title={<CardTitle icon={<FileTextOutlined />}>Información de la orden</CardTitle>}
        >
          <Row gutter={[20, 8]}>
            <Col xs={24} md={8}>
              <Form.Item label="Número PO" name="Numero_PO" rules={[{ required: true, message: "Ingrese el número PO" }]}>
                <Input size="large" placeholder="Ej: 2312055" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Cantidad" name="cantidad" rules={[{ required: true }]}>
                <InputNumber size="large" style={{ width: "100%" }} placeholder="Cantidad" min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Unidad de medida" name="oid_unidad_medida">
                <Select size="large" showSearch placeholder="Seleccione unidad" optionFilterProp="children">
                  {unidades.map(u => (
                    <Select.Option key={u.oid} value={u.oid}>{u.unidad_medida}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Tipo de producto" name="tipo_producto">
                <Input size="large" placeholder="Ej: Fertilizantes, maquinaria, servicios..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Nombre corto" name="nombre_corto">
                <Input size="large" placeholder="Nombre corto para el acta/reporte" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Nombre del producto" name="nombre_producto" rules={[{ required: true, message: "Ingrese el nombre del producto" }]}>
                <Input size="large" placeholder="Ej: Fertilizante agrícola NPK" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 20 } }}
          title={<CardTitle icon={<DollarOutlined />}>Información financiera</CardTitle>}
        >
          <Row gutter={[20, 8]}>
            <Col xs={24} md={12}>
              <Form.Item label="Valor unitario COP" name="valor_unitario_COP">
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  placeholder="Valor en pesos colombianos"
                  min={0}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Valor unitario USD" name="valor_unitario_USD">
                <InputNumber size="large" style={{ width: "100%" }} placeholder="Valor en dólares" min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 20 } }}
          title={<CardTitle icon={<ShopOutlined />}>Proveedor y estado</CardTitle>}
        >
          <Row gutter={[20, 8]}>
            <Col xs={24} md={12}>
              <Form.Item label="Proveedor" name="oid_proveedor" rules={[{ required: true }]}>
                <Select size="large" showSearch placeholder="Seleccione proveedor" optionFilterProp="children">
                  {proveedores.map(p => (
                    <Select.Option key={p.oid} value={p.oid}>{p.nombre}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Estado" name="oid_estado">
                <Select size="large" showSearch placeholder="Seleccione estado" optionFilterProp="children">
                  {estados.map(e => {
                    const estadoVisual = estadoMap[e.estado] || e.estado;
                    return <Select.Option key={e.oid} value={e.oid}>{estadoVisual}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={{ ...cardStyle, marginBottom: 0 }}
          styles={{ header: cardHeaderStyle, body: { padding: 20 } }}
          title={<CardTitle icon={<EnvironmentOutlined />}>Logística</CardTitle>}
        >
          <Row gutter={[20, 8]}>
            <Col xs={24} md={12}>
              <Form.Item label="Localización" name="localizacion">
                <Input size="large" placeholder="Ej: Bogotá - Bodega principal" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Fecha estimada de entrega" name="fecha_estimada_entrega">
                <DatePicker size="large" style={{ width: "100%" }} placeholder="Seleccione fecha" />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </Modal>
  )
}
